import { gql } from "@apollo/client"
import styled from "@emotion/styled"
import { Text, theme } from "@pallet-hq/hegel"
import { VerticallyCenteredRow } from "components/atoms"
import Image from "components/Image"
import OptionalLink from "components/Link/OptionalLink"
import moment from "moment"
import { WelcomePreviewSection_applylist } from "__generated__/WelcomePreviewSection_applylist"
import FullCandidatePreview from "./CandidatePreview/Full"

const NUM_COMPANIES = 5
const NUM_CANDIDATES = 3

const PreviewSectionFragment = gql`
    fragment WelcomePreviewSection_applylist on ApplylistType {
        id
        group {
            id
            lastDroppedAt
            upsellTotalActiveCandidates
            featuredCandidateCompanies: featuredCompanies(
                numCompanies: 5
                useCandidates: true
            ) {
                id
                image
                name
                website
            }
            fullFeaturedCandidates: featuredPublicCandidates(
                numCandidates: 3
                includeUndropped: true
            ) {
                id
                ...WelcomeFullCandidatePreview_candidate
            }
        }
    }
    ${FullCandidatePreview.fragment}
`

type PreviewSectionProps = {
    applylist: WelcomePreviewSection_applylist
}

const PreviewSection = ({ applylist }: PreviewSectionProps) => {
    const group = applylist.group!

    return (
        <Wrapper>
            <VerticallyCenteredRow>
                {group.upsellTotalActiveCandidates > 0 && (
                    <Text variant="uiLarge600">
                        {group.upsellTotalActiveCandidates} active candidates
                    </Text>
                )}
                {group.lastDroppedAt && (
                    <Text variant="uiTiny500" color="gray600" ml="auto">
                        Updated {moment(group.lastDroppedAt).fromNow()}
                    </Text>
                )}
            </VerticallyCenteredRow>
            <CandidatePreviews>
                {group.fullFeaturedCandidates
                    ?.slice(0, NUM_CANDIDATES)
                    .map(
                        candidate =>
                            candidate && (
                                <FullCandidatePreview
                                    key={candidate.id}
                                    candidate={candidate}
                                />
                            )
                    )}
            </CandidatePreviews>
            {Number(group.featuredCandidateCompanies?.length) > 1 && (
                <>
                    <Text
                        variant="uiSmall500"
                        color="gray600"
                        textTransform="uppercase"
                        textAlign="center"
                        mb="medium"
                    >
                        Hire candidates from top companies
                    </Text>
                    <Companies>
                        {group.featuredCandidateCompanies
                            ?.slice(0, NUM_COMPANIES)
                            .map(
                                company =>
                                    company && (
                                        <OptionalLink
                                            key={company.id}
                                            href={company.website}
                                            title={company.name}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <LogoImage
                                                src={company.image}
                                                alt={company.name}
                                                colorSlug=""
                                                width={64}
                                                height={64}
                                            />
                                        </OptionalLink>
                                    )
                            )}
                    </Companies>
                </>
            )}
        </Wrapper>
    )
}

PreviewSection.fragment = PreviewSectionFragment
export default PreviewSection

const Wrapper = styled.section`
    width: 100%;
    max-width: 740px;
    margin: ${theme.space.xlarge} auto;
    padding: 0 ${theme.space.small};
    box-sizing: border-box;
`

const CandidatePreviews = styled.div`
    position: relative;
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.08);
    margin: ${theme.space.small} 0 ${theme.space.xxlarge};

    & > div:first-of-type {
        border-radius: 8px 8px 0 0;
    }

    &::after {
        content: "";
        position: absolute;
        pointer-events: none;
        z-index: 1;
        inset: 0;
        background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 1) 100%
        );
        width: 100%;
        height: calc(100% + 16px);
    }
`

const Companies = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;

    & > * {
        margin: ${theme.space.small} ${theme.space.medium};
    }
`

const LogoImage = styled(Image)`
    object-fit: contain;
    border-radius: 100%;
`
